import { useMemo, type ReactNode } from 'react'
import * as THREE from 'three'
import { comprimento, vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import {
  Y_BASE_LATERAL,
  Y_TAMPA_TRASEIRA_BAIXA,
  montanteColunaC,
  raioArcoRoda,
  xDaEstruturaNaAltura,
  zDaColunaNaAltura,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { CORES_AUTOMOTIVAS, TONS_CARROCERIA } from '../../dominio/didatico/tokens'
import { MaterialPeca, MaterialVidro, Peca } from '../componentes/Peca'
import { pontoMedio, orientacaoEntre } from '../componentes/Barra'
import {
  BEVEL_CHAPA,
  OPCOES_EXTRUSAO,
  contornoChapaColunaC,
  contornoVidroCabine,
  formaLateralCarroceria,
} from './formasCarroceria'
import { geometriaVidroLateral, xDoVidroNaCintura } from './vidrosLaterais'
import { geometriaVidroTransversal } from './vidrosTransversais'
import { FaroisVeiculo } from './FaroisVeiculo'
import { LanternasVeiculo } from './LanternasVeiculo'
import { RodaRealista } from './RodaRealista'
import type { EstadoPortaOficina } from '../../dominio/didatico/oficinaCorte'
import type { EstadoTampaOficina, IdVinculoTampa } from '../../dominio/didatico/oficinaPortaMalas'
import { zonasOperacaoTampa } from '../oficina/zonasOperacaoTampa'
import { Barra } from '../componentes/Barra'
import { MaterialDeCorte } from './Seccionado'
import type { TipoAlvoImpacto } from '../../dominio/didatico/alvosImpacto'
import type { EstadoDeformacaoVeiculo } from '../../dominio/didatico/campoDeformacao'
import { Y_BASE_FASCIA, baseCapo, baseParaChoque, repousoCapo } from './geometriaDeformacaoFrontal'
import { baseLateralDensa, basePortaSolida } from './geometriaDeformacaoLateral'
import { baseTampaTraseira, extremosTampaTraseira, repousoPainelInclinado } from './geometriaDeformacaoTraseira'
import { baseTeto, repousoTeto } from './geometriaDeformacaoTeto'
import {
  MapeamentoEstrutural,
  ProvedorDeformacao,
  TransformacaoPeca,
  MalhaDeformavel,
  useDeformacao,
  useDeformacaoPeca,
  useDeslocamentoRigido,
  useGeometriaDeformada,
  useMapeamentoEstrutural,
} from './contextoDeformacao'
import { DanoVidros } from './DanoVidros'
import { DanoRiscos } from './DanoRiscos'

/**
 * Deformação frontal simples, da API anterior ao campo de deformação.
 *
 * Continua aceita: vira um estado de campo único (frente, centrado). A
 * Cinemática passa o estado completo por `ProvedorDeformacao`.
 */
export interface DeformacaoFrontalVisual {
  intrusaoM: number
  larguraM: number
  tipoAlvo: TipoAlvoImpacto
  progresso: number
}

export function estadoDaDeformacaoFrontal(deformacao: DeformacaoFrontalVisual): EstadoDeformacaoVeiculo {
  const nivel = deformacao.intrusaoM >= 0.5 ? 'severo' : deformacao.intrusaoM >= 0.28 ? 'moderado' : 'leve'
  return {
    veiculoId: 'legado',
    chave: `frente:${deformacao.intrusaoM.toFixed(4)}:${deformacao.tipoAlvo}:${deformacao.larguraM}`,
    campos:
      deformacao.intrusaoM <= 0.001
        ? []
        : [
            {
              regiao: 'frente',
              alvo: deformacao.tipoAlvo,
              variante: 'impacto',
              nivel,
              intrusaoM: deformacao.intrusaoM,
              intrusaoMaxM: deformacao.intrusaoM,
              progresso: deformacao.progresso,
              larguraM: deformacao.larguraM,
              centroFaceM: 0,
              alturaContatoY: 0.52,
              sinalLateral: -1,
              smallOverlap: false,
              assimetria: 0,
              riscos: false,
            },
          ],
  }
}

const ESPESSURA_CHAPA = 0.05
const Y_TAMPA_BAIXA = Y_TAMPA_TRASEIRA_BAIXA
const LARGURA_COLUNA_ALVO = 0.09

/**
 * X de uma chapa extrudada encostada por dentro da lateral `sinal`.
 *
 * A extrusão cresce para −X depois da rotação de −90° em Y, então o lado
 * esquerdo precisa começar deslocado pela própria espessura. Espelhar o grupo
 * com `scale` não serve: isso inverteria a silhueta no eixo longitudinal e
 * jogaria as duas chapas do mesmo lado do carro.
 */
export const xChapaLateral = (
  perfil: PerfilVeiculo,
  sinal: number,
  espessura: number,
  bevel = 0,
): number =>
  sinal > 0 ? perfil.largura / 2 - bevel : -perfil.largura / 2 + espessura + bevel

/**
 * Cache de geometria por perfil.
 *
 * `useMemo` memoriza por instância, e no Table top há vários veículos em cena:
 * eram N extrusões idênticas para os mesmos dois perfis. Com o bevel ligado a
 * malha ficou mais pesada, o que tornou o desperdício mais caro. São dois
 * perfis fixos, então o mapa nunca cresce.
 */
const GEOMETRIAS_LATERAIS = new Map<string, THREE.ExtrudeGeometry>()

function geometriaLateralDoPerfil(perfil: PerfilVeiculo): THREE.ExtrudeGeometry {
  const existente = GEOMETRIAS_LATERAIS.get(perfil.id)
  if (existente) return existente
  const criada = new THREE.ExtrudeGeometry(formaLateralCarroceria(perfil), {
    ...OPCOES_EXTRUSAO,
    depth: ESPESSURA_CHAPA,
  })
  GEOMETRIAS_LATERAIS.set(perfil.id, criada)
  return criada
}

/**
 * Relevo da lateral: para-lamas sobre as caçambas e saia da soleira.
 *
 * A chapa lateral é uma extrusão plana em X, e era daí que vinha o ar de maquete
 * de papelão — a silhueta estava certa de perfil e reta de frente. Estes dois
 * volumes dão largura ao carro.
 *
 * Ficam fora dos vãos das portas de propósito: o para-lama contorna a caçamba e
 * a saia corre abaixo da soleira, então nenhum dos dois atravessa uma porta que
 * pode abrir ou ser removida.
 */
function RelevoLateral({
  perfil,
  sinal,
  material,
  deformarParalamaDianteiro = false,
}: {
  perfil: PerfilVeiculo
  sinal: number
  material: ReactNode
  deformarParalamaDianteiro?: boolean
}) {
  const raio = raioArcoRoda(perfil)
  const x = sinal * (perfil.largura / 2 + 0.006)
  const zEntreCacambas = (perfil.eixoDianteiroZ + perfil.eixoTraseiroZ) / 2
  const comprimentoSaia = perfil.eixoDianteiroZ - perfil.eixoTraseiroZ - raio * 2
  const ySaia = (Y_BASE_LATERAL + perfil.topoSoleiraY) / 2
  const sufixo = sinal > 0 ? 'Esquerda' : 'Direita'
  // a saia acompanha a lateral quando ela entra (impacto lateral)
  const deslocamentoSaia = useDeslocamentoRigido([x, ySaia, zEntreCacambas], 'lateral')

  return (
    <group>
      {[perfil.eixoDianteiroZ, perfil.eixoTraseiroZ].map((z, indice) => (
        // meia volta de toro acompanhando o arco entalhado na silhueta; o giro
        // de 90° em Y põe o anel no plano ZY, que é o plano da lateral.
        // Na colisão, o arco do para-lama é deslocado e torcido pelo campo.
        <TransformacaoPeca
          key={z}
          pecaId={`${indice === 0 ? 'paralamaDianteiro' : 'paralamaTraseiro'}${sufixo}`}
          ancora={[x, perfil.raioRoda + raio * 0.85, z]}
        >
          <mesh
            position={[
              x + (indice === 0 && deformarParalamaDianteiro ? sinal * 0.07 : 0),
              perfil.raioRoda + (indice === 0 && deformarParalamaDianteiro ? -0.035 : 0),
              z,
            ]}
            rotation={[0, Math.PI / 2, indice === 0 && deformarParalamaDianteiro ? sinal * 0.12 : 0]}
            scale={indice === 0 && deformarParalamaDianteiro ? [1, 0.82, 1.08] : 1}
          >
            <torusGeometry args={[raio, 0.03, 8, 22, Math.PI]} />
            {material}
          </mesh>
        </TransformacaoPeca>
      ))}

      <mesh position={[x + deslocamentoSaia[0], ySaia + deslocamentoSaia[1], zEntreCacambas + deslocamentoSaia[2]]}>
        <boxGeometry args={[0.035, perfil.topoSoleiraY - Y_BASE_LATERAL - 0.06, comprimentoSaia]} />
        {material}
      </mesh>

      {deformarParalamaDianteiro && (
        <group position={[x, (perfil.topoSoleiraY + perfil.cinturaY) / 2 + 0.05, perfil.eixoDianteiroZ]}>
          {/* 1. Vinco vertical profundo em sanfona/fole no centro do para-lama acima do meio da roda */}
          <mesh position={[-sinal * 0.035, 0, 0]} rotation={[0, -sinal * 0.15, sinal * 0.08]}>
            <boxGeometry args={[0.05, 0.32, 0.18]} />
            <meshStandardMaterial color="#0f172a" roughness={0.8} metalness={0.4} />
          </mesh>

          {/* 2. Chapa externa repuxada e inclinada para a frente pela alavanca do alargador */}
          <mesh position={[sinal * 0.028, 0.01, 0.04]} rotation={[sinal * 0.08, -sinal * 0.24, -sinal * 0.15]}>
            <boxGeometry args={[0.022, 0.28, 0.16]} />
            {material}
          </mesh>

          {/* 3. Vão aberto de encolhimento (gap de ~3 cm junto à porta dianteira expondo a coluna A) */}
          <mesh
            position={[
              -sinal * 0.015,
              0,
              perfil.portaDianteira.zDianteiro - perfil.eixoDianteiroZ + 0.02,
            ]}
          >
            <boxGeometry args={[0.04, 0.38, 0.06]} />
            <meshStandardMaterial color="#020617" roughness={0.9} metalness={0.2} />
          </mesh>

          {/* 4. Dobradiças dianteiras visíveis dentro do vão recém-aberto */}
          {[
            perfil.cinturaY - 0.14 - ((perfil.topoSoleiraY + perfil.cinturaY) / 2 + 0.05),
            perfil.topoSoleiraY + 0.14 - ((perfil.topoSoleiraY + perfil.cinturaY) / 2 + 0.05),
          ].map((yDob, i) => (
            <mesh
              key={i}
              position={[
                -sinal * 0.02,
                yDob,
                perfil.portaDianteira.zDianteiro - perfil.eixoDianteiroZ + 0.01,
              ]}
            >
              <boxGeometry args={[0.035, 0.04, 0.04]} />
              <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.3} />
            </mesh>
          ))}
        </group>
      )}
    </group>
  )
}

import type { EstadoTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import { ChapaTetoLateral3D } from '../oficina/TetoLateral3D'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

interface PropsCarroceria {
  tetoLateral?: EstadoTetoLateral
  perfil: PerfilVeiculo
  modoAlvo?: boolean
  /** Permite distinguir veículos compostos no Table top sem duplicar a geometria. */
  corExterna?: string
  /** Permite manter as rodas fora do grupo móvel da carroceria (ex.: simulação de suspensão). */
  mostrarRodas?: boolean
  distanciaRodada?: number
  detalhesAnatomia?: boolean
  oficinaPorta?: {
    id: 'portaDianteiraEsquerda' | 'portaDianteiraDireita' | 'portaTraseiraEsquerda' | 'portaTraseiraDireita'
    estado: EstadoPortaOficina
  }
  oficinaTampa?: {
    estado: EstadoTampaOficina
  }
  aberturaTampa?: number
  deformacaoFrontal?: DeformacaoFrontalVisual | null
}

/**
 * Chapas externas e acabamentos automotivos detalhados: painéis laterais, capô,
 * teto, tampa traseira, vidros, para-choques, faróis com projetores LED,
 * lanternas traseiras rubi, grade dianteira, retrovisores e rodas de liga leve.
 */
export function Carroceria(props: PropsCarroceria) {
  const externo = useDeformacao()
  const { deformacaoFrontal } = props
  // a API antiga (só frente) vira um estado de campo — a menos que o veículo
  // já esteja dentro de um provedor, que é quem manda
  const estadoLegado = useMemo(
    () => (deformacaoFrontal && !externo ? estadoDaDeformacaoFrontal(deformacaoFrontal) : null),
    [deformacaoFrontal, externo],
  )
  if (estadoLegado) {
    return (
      <ProvedorDeformacao estado={estadoLegado} perfil={props.perfil}>
        <CarroceriaDeformavel {...props} />
      </ProvedorDeformacao>
    )
  }
  return <CarroceriaDeformavel {...props} />
}

function CarroceriaDeformavel({
  perfil,
  modoAlvo = false,
  corExterna,
  mostrarRodas = true,
  distanciaRodada = 0,
  detalhesAnatomia = false,
  oficinaPorta,
  oficinaTampa,
  aberturaTampa = 0,
  tetoLateral,
}: PropsCarroceria) {
  const geometriaCapo = useGeometriaDeformada(baseCapo(perfil), 'capo', repousoCapo(perfil))
  const geometriaTeto = useGeometriaDeformada(baseTeto(perfil), 'teto', repousoTeto(perfil))
  const comprimentoTeto = perfil.teto.zDianteiro - perfil.teto.zTraseiro

  const corAlvo = corExterna ?? CORES_AUTOMOTIVAS.alvoCarroceria

  if (modoAlvo) {
    return (
      <group>
        {/* Laterais sólidas em azul marinho metálico */}
        {[1, -1].map((sinal) => (
          <ChapaLateral key={sinal} perfil={perfil} sinal={sinal}>
            <meshPhysicalMaterial
              color={corAlvo}
              metalness={0.75}
              roughness={0.28}
              clearcoat={1}
              clearcoatRoughness={0.06}
              envMapIntensity={1}
              side={THREE.DoubleSide}
            />
          </ChapaLateral>
        ))}

        {[1, -1].map((sinal) => (
          <RelevoLateral
            key={`relevo:${sinal}`}
            perfil={perfil}
            sinal={sinal}
            deformarParalamaDianteiro={
              (oficinaPorta?.estado.paralama === 'deformado'
              && oficinaPorta.id.startsWith('portaDianteira')
              && (oficinaPorta.id.endsWith('Esquerda') ? sinal < 0 : sinal > 0))
            }
            material={<PinturaAlvo cor={corAlvo} />}
          />
        ))}

        {/* Capô sólido */}
        <mesh
          geometry={geometriaCapo}
          position={[0, perfil.capoY, (perfil.colunaA.zBase + perfil.frenteZ) / 2 + 0.05]}
          rotation={[-0.035, 0, 0]}
        >
          <PinturaAlvo cor={corExterna ?? CORES_AUTOMOTIVAS.alvoCapo} />
        </mesh>

        {/* Teto sólido */}
        <mesh geometry={geometriaTeto} position={repousoTeto(perfil).posicao as [number, number, number]}>
          <PinturaAlvo cor={corExterna ?? CORES_AUTOMOTIVAS.alvoTeto} />
        </mesh>

        {/* Tampa traseira */}
        <TampaTraseira perfil={perfil} corAlvo={corAlvo} />

        {/* Para-choques sólidos */}
        <Fascia
          id="paraChoqueDianteiro"
          z={perfil.frenteZ - 0.05}
          yTopo={perfil.capoY}
          perfil={perfil}
          corAlvo={corAlvo}
        />
        <Fascia
          z={perfil.traseiraZ + 0.05}
          yTopo={perfil.volumeTraseiro === 'hatch' ? Y_TAMPA_BAIXA : perfil.cinturaY}
          perfil={perfil}
          corAlvo={corAlvo}
        />

        {/* Vidros escurecidos fumê */}
        <ParaBrisa perfil={perfil} corAlvo={corAlvo} />
        <VidroTraseiro perfil={perfil} corAlvo={corAlvo} />

        {/* Cabine e portas fechadas: a silhueta lateral recorta os vãos das
            portas e para na linha de cintura. Sem estas peças o alvo ficava
            com dois buracos na lateral e o teto flutuando sobre o nada. */}
        <PortasSolidas perfil={perfil} cor={corAlvo} />
        <MapeamentoEstrutural camada="estrutura">
          <CabineSolida perfil={perfil} cor={corAlvo} />
        </MapeamentoEstrutural>

        {/* Assoalho inferior contínuo: fecha a parte de baixo do veículo entre as soleiras */}
        <AssoalhoCarroceria perfil={perfil} cor={corAlvo} modoAlvo />

        {/* Detalhes automotivos completos */}
        <FaroisVeiculo perfil={perfil} aceso />
        <LanternasVeiculo perfil={perfil} aceso />
        <GradeFrontal perfil={perfil} />
        <Retrovisores perfil={perfil} modoAlvo corExterna={corAlvo} />
        {mostrarRodas && <RodaRealista perfil={perfil} modoAlvo distanciaRodada={distanciaRodada} />}
        <DanoVidros perfil={perfil} />
        <DanoRiscos perfil={perfil} />
      </group>
    )
  }

  return (
    <group>
      {/* painéis laterais com suporte a explosão didática e controle de opacidade */}
      {[1, -1].map((sinal) => (
        <Peca
          key={sinal}
          id={sinal < 0 ? 'paineisLateraisEsquerda' : 'paineisLateraisDireita'}
          deslocamentoAfastado={[sinal * 0.55, 0, 0]}
        >
          <ChapaLateral perfil={perfil} sinal={sinal}>
            <MaterialPeca
              id={sinal < 0 ? 'paineisLateraisEsquerda' : 'paineisLateraisDireita'}
              corBase={TONS_CARROCERIA.painelLateral}
              ladoDuplo
              acabamento="pintura"
            />
          </ChapaLateral>

          {/* dentro da mesma Peca: para-lama e saia acompanham o painel lateral
              quando ele é afastado, ocultado ou tornado transparente */}
          <RelevoLateral
            perfil={perfil}
            sinal={sinal}
            deformarParalamaDianteiro={
              (oficinaPorta?.estado.paralama === 'deformado'
              && oficinaPorta.id.startsWith('portaDianteira')
              && (oficinaPorta.id.endsWith('Esquerda') ? sinal < 0 : sinal > 0))
            }
            material={
              <MaterialPeca
                id={sinal < 0 ? 'paineisLateraisEsquerda' : 'paineisLateraisDireita'}
                corBase={TONS_CARROCERIA.painelLateral}
                acabamento="pintura"
              />
            }
          />

          <ChapaColunaC perfil={perfil} sinal={sinal}>
            <MaterialPeca
              id={sinal < 0 ? 'paineisLateraisEsquerda' : 'paineisLateraisDireita'}
              corBase={TONS_CARROCERIA.painelLateral}
              ladoDuplo
              acabamento="pintura"
            />
          </ChapaColunaC>
        </Peca>
      ))}

      {/* O capô articula na quina traseira, junto ao para-brisa, como o de
          verdade. Abrir o capô é o que dá acesso visual ao cofre do motor: sem
          giro, "abrir" só afastava a chapa e não mostrava nada embaixo. */}
      <Peca
        id="capo"
        position={[0, perfil.capoY, perfil.colunaA.zBase]}
        deslocamentoAfastado={[0, 0.5, 0.25]}
        aberturaRad={-0.95}
        eixoAbertura="x"
      >
        <group position={[0, -perfil.capoY, -perfil.colunaA.zBase]}>
          <mesh
            geometry={geometriaCapo}
            position={[0, perfil.capoY, (perfil.colunaA.zBase + perfil.frenteZ) / 2 + 0.05]}
            rotation={[-0.035, 0, 0]}
          >
            <MaterialPeca id="capo" corBase={TONS_CARROCERIA.capo} acabamento="pintura" />
          </mesh>
        </group>
      </Peca>

      {tetoLateral ? <ChapaTetoLateral3D perfil={perfil} estado={tetoLateral} /> : <Peca
        id="teto"
        position={[0, perfil.tetoY, perfil.teto.zTraseiro]}
        deslocamentoAfastado={[0, 0.6, 0]}
        aberturaRad={-2.95}
        eixoAbertura="x"
      >
        <mesh geometry={geometriaTeto} position={[0, 0, comprimentoTeto / 2]}>
          <MaterialPeca id="teto" corBase={TONS_CARROCERIA.teto} acabamento="pintura" />
        </mesh>
      </Peca>}

      <TampaTraseira
        perfil={perfil}
        estadoOficina={oficinaTampa?.estado}
        aberturaTampa={aberturaTampa}
      />

      <Fascia
        id="paraChoqueDianteiro"
        perfil={perfil}
        z={perfil.frenteZ - 0.05}
        yTopo={perfil.capoY}
        afastamento={0.6}
      />
      <Fascia
        id="paraChoqueTraseiro"
        perfil={perfil}
        z={perfil.traseiraZ + 0.05}
        yTopo={perfil.volumeTraseiro === 'hatch' ? Y_TAMPA_BAIXA : perfil.cinturaY}
        afastamento={-0.6}
      />

      {!tetoLateral?.vidroCortado && <ParaBrisa perfil={perfil} />}
      <VidroTraseiro perfil={perfil} />
      <VigiasLaterais perfil={perfil} />

      {/* Assoalho inferior da carroceria: fecha o fundo sob o habitáculo e porta-malas */}
      <AssoalhoCarroceria perfil={perfil} />

      {/* Detalhes automotivos avançados */}
      <DetalheExterno id="farois" ativo={detalhesAnatomia}><FaroisVeiculo perfil={perfil} /></DetalheExterno>
      <DetalheExterno id="lanternas" ativo={detalhesAnatomia}><LanternasVeiculo perfil={perfil} /></DetalheExterno>
      <DetalheExterno id="gradeFrontal" ativo={detalhesAnatomia}><GradeFrontal perfil={perfil} /></DetalheExterno>
      <DetalheExterno id="retrovisores" ativo={detalhesAnatomia}><Retrovisores perfil={perfil} /></DetalheExterno>
      {mostrarRodas && <DetalheExterno id="rodas" ativo={detalhesAnatomia}><RodaRealista perfil={perfil} distanciaRodada={distanciaRodada} /></DetalheExterno>}
      <DanoVidros perfil={perfil} />
      <DanoRiscos perfil={perfil} />
    </group>
  )
}

/**
 * Chapa lateral extrudada. Intacta, é a geometria cacheada do perfil; com
 * colisão, um clone da versão densificada passa pelo campo (para-lama que
 * encurta e se aproxima da roda, lateral que entra num impacto lateral).
 */
function ChapaLateral({ perfil, sinal, children }: { perfil: PerfilVeiculo; sinal: number; children: ReactNode }) {
  const deformando = useDeformacao() !== null
  const base = deformando ? baseLateralDensa(perfil, ESPESSURA_CHAPA) : geometriaLateralDoPerfil(perfil)
  const geometria = useGeometriaDeformada(base, 'lateral', {
    posicao: [xChapaLateral(perfil, sinal, ESPESSURA_CHAPA, BEVEL_CHAPA), 0, 0],
    rotacao: [0, -Math.PI / 2, 0],
  })
  return (
    <mesh
      geometry={geometria}
      position={[xChapaLateral(perfil, sinal, ESPESSURA_CHAPA, BEVEL_CHAPA), 0, 0]}
      rotation={[0, -Math.PI / 2, 0]}
    >
      {children}
    </mesh>
  )
}

/** Chapas das portas fechadas, preenchendo os vãos recortados na lateral. */
function PortasSolidas({ perfil, cor }: { perfil: PerfilVeiculo; cor: string }) {
  const faixas = [perfil.portaDianteira, perfil.portaTraseira]

  return (
    <group>
      {faixas.flatMap((faixa, indice) =>
        [1, -1].map((sinal) => (
          <PortaSolida key={`${indice}:${sinal}`} perfil={perfil} faixa={faixa} sinal={sinal} cor={cor} />
        )),
      )}
    </group>
  )
}

function PortaSolida({
  perfil,
  faixa,
  sinal,
  cor,
}: {
  perfil: PerfilVeiculo
  faixa: PerfilVeiculo['portaDianteira']
  sinal: number
  cor: string
}) {
  const altura = perfil.cinturaY - perfil.topoSoleiraY
  const posicao: [number, number, number] = [
    sinal * (perfil.largura / 2 - ESPESSURA_CHAPA / 2),
    perfil.topoSoleiraY + altura / 2,
    (faixa.zDianteiro + faixa.zTraseiro) / 2,
  ]
  // menor que o vão: a fresta que sobra é o recorte da porta
  const geometria = useGeometriaDeformada(basePortaSolida(perfil, faixa, ESPESSURA_CHAPA), 'porta', { posicao })
  return (
    <mesh geometry={geometria} position={posicao}>
      <PinturaAlvo cor={cor} />
    </mesh>
  )
}

/** Colunas A/B/C e vidros laterais: o que sustenta e fecha o teto do alvo. */
function CabineSolida({ perfil, cor }: { perfil: PerfilVeiculo; cor: string }) {
  return (
    <group>
      {[1, -1].map((sinal) => {
        return (
          <group key={sinal}>
            {/* Vãos separados pela B e pelo batente traseiro; não há vidro atravessando as colunas. */}
            {(['dianteiro', 'traseiro', 'vigia'] as const).map((vao) => (
              <VidroCabineAlvo key={vao} id={vao === 'vigia' ? null : `vidro${vao === 'dianteiro' ? 'Dianteiro' : 'Traseiro'}${sinal > 0 ? 'Esquerda' : 'Direita'}`}>
              <MalhaDeformavel
                camada="estrutura"
                posicao={[xDoVidroNaCintura(perfil, sinal), 0, 0]}
                rotacao={[0, -Math.PI / 2, 0]}
                base={geometriaVidroLateral(perfil, sinal, contornoVidroCabine(perfil, vao))}
              >
                <meshStandardMaterial
                  color={CORES_AUTOMOTIVAS.alvoVidroFume}
                  roughness={0.06}
                  metalness={0.12}
                  envMapIntensity={1.4}
                  transparent
                  opacity={0.30}
                  depthWrite={false}
                  side={THREE.DoubleSide}
                />
              </MalhaDeformavel>
              </VidroCabineAlvo>
            ))}

            <ColunaSolida
              cor={cor}
              perfil={perfil} sinal={sinal} coluna={perfil.colunaA}
            />
            <ColunaSolida
              cor={cor}
              perfil={perfil} sinal={sinal}
              coluna={{ zBase: perfil.colunaB.z, zTopo: perfil.colunaB.z }}
            />
            <ColunaSolida
              cor={cor}
              perfil={perfil} sinal={sinal} coluna={montanteColunaC(perfil)}
            />
            <ChapaColunaC perfil={perfil} sinal={sinal}>
              <PinturaAlvo cor={cor} />
            </ChapaColunaC>
          </group>
        )
      })}
    </group>
  )
}

/** Chapa larga da coluna C, entre o montante e o vidro traseiro, no plano dos vidros laterais. */
function ChapaColunaC({ perfil, sinal, children }: { perfil: PerfilVeiculo; sinal: number; children: ReactNode }) {
  return (
    <MalhaDeformavel
      camada="estrutura"
      posicao={[xDoVidroNaCintura(perfil, sinal) + sinal * 0.012, 0, 0]}
      rotacao={[0, -Math.PI / 2, 0]}
      base={geometriaVidroLateral(perfil, sinal, contornoChapaColunaC(perfil))}
    >
      {children}
    </MalhaDeformavel>
  )
}

/** Vidro do alvo: some quando o domínio diz que rompeu. */
function VidroCabineAlvo({ id, children }: { id: string | null; children: ReactNode }) {
  const peca = useDeformacaoPeca(id ?? '')
  if (id && peca?.vidro === 'rompido') return null
  return <>{children}</>
}

function ColunaSolida({ perfil, sinal, coluna, cor }: {
  perfil: PerfilVeiculo
  sinal: number
  coluna: { zBase: number; zTopo: number }
  cor: string
}) {
  const mapeamento = useMapeamentoEstrutural()
  const pontos = Array.from({ length: 5 }, (_, i) => {
    const y = perfil.cinturaY - 0.04 + (perfil.tetoY - perfil.cinturaY + 0.04) * i / 4
    const p = vetor(xDaEstruturaNaAltura(perfil, sinal, y), y, zDaColunaNaAltura(perfil, coluna, y))
    return mapeamento ? mapeamento.mapear(p) : p
  })
  return <group>{pontos.slice(0, -1).map((de, i) => (
    <TrechoColunaSolida key={i} de={de} para={pontos[i + 1]} cor={cor} />
  ))}</group>
}

function TrechoColunaSolida({ de, para, cor }: { de: Vetor3; para: Vetor3; cor: string }) {
  const orientacao = useMemo(() => orientacaoEntre(de, para), [de, para])
  const centro = pontoMedio(de, para)
  const altura = comprimento({ x: para.x - de.x, y: para.y - de.y, z: para.z - de.z }) + 0.04

  return (
    <mesh position={[centro.x, centro.y, centro.z]} quaternion={orientacao}>
      <boxGeometry args={[LARGURA_COLUNA_ALVO, altura, LARGURA_COLUNA_ALVO]} />
      <meshStandardMaterial color={cor} metalness={0.7} roughness={0.3} />
    </mesh>
  )
}

function GradeFrontal({ perfil }: { perfil: PerfilVeiculo }) {
  const altura = 0.14
  const largura = 0.62
  const z = perfil.frenteZ - 0.04
  const y = (perfil.capoY + Y_BASE_FASCIA) / 2 + 0.06

  return (
    <TransformacaoPeca pecaId="grade" ancora={[0, y, z]}>
    <group position={[0, y, z]}>
      {/* Moldura da grade em grafite escuro */}
      <mesh>
        <CaixaArredondada args={[largura, altura, 0.04]} />
        <meshStandardMaterial
          color={CORES_AUTOMOTIVAS.gradeMoldura}
          roughness={0.45}
          metalness={0.7}
          envMapIntensity={1.1}
        />
      </mesh>
      {/* Miolo em colmeia / aletas pretas */}
      <mesh position={[0, 0, 0.015]}>
        <CaixaArredondada args={[largura * 0.92, altura * 0.78, 0.02]} />
        <meshStandardMaterial color={CORES_AUTOMOTIVAS.gradeColmeia} roughness={0.9} metalness={0.1} />
      </mesh>
      {/* Tomada de ar inferior no para-choque */}
      <mesh position={[0, -altura * 1.55, 0]}>
        <CaixaArredondada args={[largura * 1.15, altura * 0.6, 0.03]} />
        <meshStandardMaterial color="#0b0f14" roughness={0.92} metalness={0.1} />
      </mesh>
    </group>
    </TransformacaoPeca>
  )
}

function Retrovisores({
  perfil,
  modoAlvo = false,
  corExterna,
}: {
  perfil: PerfilVeiculo
  modoAlvo?: boolean
  corExterna?: string
}) {
  const mx = perfil.largura / 2 + 0.085
  const y = perfil.cinturaY + 0.06
  const z = perfil.colunaA.zBase - 0.04
  const corCapa = modoAlvo
    ? corExterna ?? CORES_AUTOMOTIVAS.alvoCarroceria
    : TONS_CARROCERIA.painelLateral

  return (
    <group>
      {[-1, 1].map((sinal) => (
        <TransformacaoPeca key={sinal} pecaId={`retrovisor${sinal > 0 ? 'Esquerda' : 'Direita'}`} ancora={[sinal * mx, y, z]}>
        <group position={[sinal * mx, y, z]} rotation={[0, -sinal * 0.18, 0]}>
          {/* Braço de suporte fixado na porta/coluna */}
          <mesh position={[-sinal * 0.04, -0.02, 0]}>
            <CaixaArredondada args={[0.07, 0.025, 0.04]} />
            <meshStandardMaterial color="#0f172a" roughness={0.7} metalness={0.3} />
          </mesh>
          {/* Carcaça aerodinâmica externa */}
          <mesh position={[0, 0, 0]}>
            <CaixaArredondada args={[0.09, 0.075, 0.14]} />
            <meshPhysicalMaterial
              color={corCapa}
              roughness={0.28}
              metalness={0.75}
              clearcoat={1}
              clearcoatRoughness={0.06}
              envMapIntensity={1}
            />
          </mesh>
          {/* Superfície espelhada voltada para trás */}
          <mesh position={[0, 0, -0.065]}>
            <CaixaArredondada args={[0.075, 0.06, 0.008]} />
            <meshStandardMaterial
              color={CORES_AUTOMOTIVAS.espelhoRetrovisor}
              metalness={0.95}
              roughness={0.05}
              envMapIntensity={1.5}
            />
          </mesh>
        </group>
        </TransformacaoPeca>
      ))}
    </group>
  )
}

/**
 * Pintura do veículo-alvo.
 *
 * O alvo não é peça didática selecionável, então não passa por `MaterialPeca`.
 * Mesmo assim precisa do mesmo verniz, senão o carro colidente lê como plástico
 * ao lado do veículo da maquete.
 */
function PinturaAlvo({ cor }: { cor: string }) {
  return (
    <meshPhysicalMaterial
      color={cor}
      metalness={0.75}
      roughness={0.28}
      clearcoat={1}
      clearcoatRoughness={0.06}
      envMapIntensity={1}
    />
  )
}

/** Vidro fumê do alvo, que também não passa pelo contexto da maquete. */
function VidroFumeAlvo() {
  return (
    <meshStandardMaterial
      color={CORES_AUTOMOTIVAS.alvoVidroFume}
      roughness={0.06}
      metalness={0.12}
      envMapIntensity={1.4}
      transparent
      opacity={0.32}
      depthWrite={false}
      side={THREE.DoubleSide}
    />
  )
}

/** Envolve em `Peca` só quando a peça é didática; o alvo entra cru. */
function TalvezPeca({
  peca,
  children,
}: {
  peca?: { id: string; deslocamentoAfastado: [number, number, number] }
  children: ReactNode
}) {
  if (!peca) return <>{children}</>
  return (
    <Peca id={peca.id} deslocamentoAfastado={peca.deslocamentoAfastado}>
      {children}
    </Peca>
  )
}

function ParaBrisa({ perfil, corAlvo }: { perfil: PerfilVeiculo; corAlvo?: string }) {
  const geometria = useMemo(() => geometriaVidroTransversal(perfil, 'frente'), [perfil])
  return (
    <TalvezPeca peca={corAlvo ? undefined : { id: 'paraBrisa', deslocamentoAfastado: [0, 0.35, 0.45] }}>
      <MalhaDeformavel base={geometria} camada="estrutura">
        {corAlvo ? <VidroFumeAlvo /> : <MaterialVidro id="paraBrisa" />}
      </MalhaDeformavel>
    </TalvezPeca>
  )
}

function VidroTraseiro({ perfil, corAlvo }: { perfil: PerfilVeiculo; corAlvo?: string }) {
  const geometria = useMemo(() => geometriaVidroTransversal(perfil, 'traseira'), [perfil])
  // temperado: na traseira severa ele se desfaz em vez de trincar
  if (useDeformacaoPeca('vidroTraseiro')?.vidro === 'rompido') return null
  return (
    <TalvezPeca
      peca={corAlvo ? undefined : { id: 'vidroTraseiro', deslocamentoAfastado: [0, 0.35, -0.45] }}
    >
      <MalhaDeformavel base={geometria} camada="estrutura">
        {corAlvo ? <VidroFumeAlvo /> : <MaterialVidro id="vidroTraseiro" />}
      </MalhaDeformavel>
    </TalvezPeca>
  )
}

/** Vidros fixos do quarto traseiro, independentes dos vidros móveis das portas. */
function VigiasLaterais({ perfil }: { perfil: PerfilVeiculo }) {
  return <group>{([1, -1] as const).map((sinal) => {
    const id = sinal > 0 ? 'vidroVigiaEsquerda' : 'vidroVigiaDireita'
    return <Peca key={id} id={id} deslocamentoAfastado={[sinal * 0.5, 0.15, 0]}>
      <MalhaDeformavel camada="estrutura" posicao={[xDoVidroNaCintura(perfil, sinal), 0, 0]}
        rotacao={[0, -Math.PI / 2, 0]}
        base={geometriaVidroLateral(perfil, sinal, contornoVidroCabine(perfil, 'vigia'))}>
        <MaterialVidro id={id} />
      </MalhaDeformavel>
    </Peca>
  })}</group>
}

/**
 * Tampa traseira.
 *
 * Articula na quina de cima: no hatch é a travessa do teto, no sedan é a
 * travessa do baú. Abrir a tampa é pré-requisito de saída pela traseira, então
 * ela precisa mesmo girar, não só sumir.
 */
function TampaTraseira({
  perfil,
  corAlvo,
  estadoOficina,
  aberturaTampa = 0,
}: {
  perfil: PerfilVeiculo
  corAlvo?: string
  estadoOficina?: EstadoTampaOficina
  aberturaTampa?: number
}) {
  const { de, para } = extremosTampaTraseira(perfil)
  const repouso = repousoPainelInclinado(de, para)
  // a tampa entra por baixo e dobra — mesma pose do painel inclinado de sempre
  const geometria = useGeometriaDeformada(baseTampaTraseira(perfil, perfil.largura - 0.1, 0.05), 'tampaTraseira', repouso)

  if (estadoOficina?.removida) return null

  const deformacaoChapa = estadoOficina?.chapa === 'deformada' ? 0.06 : 0

  const corpo = (
    <group position={[deformacaoChapa, 0, 0]}>
      <mesh
        geometry={geometria}
        position={repouso.posicao as [number, number, number]}
        quaternion={repouso.quaternion as [number, number, number, number]}
      >
        {corAlvo ? (
          <PinturaAlvo cor={corAlvo} />
        ) : (
          <MaterialPeca id="tampaTraseira" corBase={TONS_CARROCERIA.tampaTraseira} acabamento="pintura" />
        )}
      </mesh>
    </group>
  )

  if (corAlvo) return corpo

  // A oficina de corte comanda a própria rotação de abertura (zerando
  // `aberturaRad`, que pertence à explosão didática da Estrutura) e mostra os
  // vínculos que a peça de detalhe fixa (dobradiças, trava, apoio a gás) nas
  // mesmas coordenadas usadas pelos marcadores de operação — cortado, cada um
  // deles fica onde estava, em duas partes, sem duplicar geometria.
  const anguloAberturaTampa = estadoOficina
    ? -1.35 * estadoOficina.abertura
    : -1.35 * aberturaTampa
  // o vínculo cortado fica onde estava, seccionado — ver `Seccionado.tsx`
  const vinculoCortado = (vinculo: IdVinculoTampa) => estadoOficina?.vinculos[vinculo] === 'liberado'
  const zonas = estadoOficina ? zonasOperacaoTampa(perfil) : []
  const zonaPor = (id: IdVinculoTampa | 'chapa') => zonas.find((zona) => zona.id === id)?.posicao

  // a Peca fica no eixo da dobradiça e o corpo é desenhado em coordenada de
  // mundo; por isso o grupo interno desfaz a translação do eixo
  return (
    <Peca
      id="tampaTraseira"
      position={[0, de.y, de.z]}
      deslocamentoAfastado={[0, 0.2, -0.65]}
      aberturaRad={estadoOficina || aberturaTampa > 0 ? 0 : -1.35}
      eixoAbertura="x"
      rotation={[anguloAberturaTampa, 0, 0]}
    >
      <group position={[0, -de.y, -de.z]}>
        {corpo}
        {estadoOficina && zonaPor('dobradicaEsquerda') && (
          <DobradicaTampa
            posicao={zonaPor('dobradicaEsquerda')!}
            cortada={vinculoCortado('dobradicaEsquerda')}
          />
        )}
        {estadoOficina && zonaPor('dobradicaDireita') && (
          <DobradicaTampa
            posicao={zonaPor('dobradicaDireita')!}
            cortada={vinculoCortado('dobradicaDireita')}
          />
        )}
        {estadoOficina && zonaPor('trava') && (
          vinculoCortado('trava') ? (
            <TravaTampaCortada posicao={zonaPor('trava')!} />
          ) : (
            <mesh position={zonaPor('trava')}>
              <CaixaArredondada args={[0.08, 0.05, 0.06]} />
              <MaterialPeca id="tampaTraseira.trava" metalico={0.5} rugosidade={0.45} />
            </mesh>
          )
        )}
        {estadoOficina && zonaPor('apoio') && zonaPor('dobradicaDireita') && (
          <ApoioAGas
            base={[zonaPor('dobradicaDireita')![0], zonaPor('dobradicaDireita')![1], zonaPor('dobradicaDireita')![2] - 0.08]}
            ponta={zonaPor('apoio')!}
            cortado={vinculoCortado('apoio')}
          />
        )}
      </group>
    </Peca>
  )
}

/** Dobradiça da tampa: pino horizontal (a tampa gira em X) com as duas folhas. */
function DobradicaTampa({
  posicao,
  cortada = false,
}: {
  posicao: [number, number, number]
  cortada?: boolean
}) {
  return (
    <group position={posicao}>
      {cortada ? (
        [1, -1].map((metade) => (
          <group key={metade} position={[metade * 0.036, 0, 0]}>
            <mesh rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.024, 0.024, 0.045, 12]} />
              <MaterialPeca id="tampaTraseira.dobradica" metalico={0.5} rugosidade={0.4} />
            </mesh>
            <mesh position={[-metade * 0.0225, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.0245, 0.0245, 0.006, 12]} />
              <MaterialDeCorte />
            </mesh>
          </group>
        ))
      ) : (
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.024, 0.024, 0.1, 12]} />
          <MaterialPeca id="tampaTraseira.dobradica" metalico={0.5} rugosidade={0.4} />
        </mesh>
      )}
      <mesh position={[0, -0.02, -0.03]}>
        <CaixaArredondada args={[0.07, 0.02, 0.07]} />
        <MaterialPeca id="tampaTraseira.dobradica" metalico={0.5} rugosidade={0.4} />
      </mesh>
      <mesh position={[0, 0.02, 0.03]}>
        <CaixaArredondada args={[0.07, 0.02, 0.07]} />
        <MaterialPeca id="tampaTraseira.dobradica" metalico={0.5} rugosidade={0.4} />
      </mesh>
    </group>
  )
}

/** Trava da tampa depois do corte: duas metades com o aço vivo entre elas. */
function TravaTampaCortada({ posicao }: { posicao: [number, number, number] }) {
  return (
    <group position={posicao}>
      {[1, -1].map((metade) => (
        <group key={metade} position={[0, metade * 0.018, 0]}>
          <mesh>
            <CaixaArredondada args={[0.08, 0.018, 0.06]} />
            <MaterialPeca id="tampaTraseira.trava" metalico={0.5} rugosidade={0.45} />
          </mesh>
          <mesh position={[0, -metade * 0.009, 0]}>
            <CaixaArredondada args={[0.081, 0.005, 0.061]} />
            <MaterialDeCorte />
          </mesh>
        </group>
      ))}
    </group>
  )
}

/**
 * Amortecedor a gás da tampa, inteiro ou cortado.
 *
 * Cortado, ele não some: fica como dois tocos com a folga entre eles, que é o
 * que o aluno precisa reconhecer antes de manusear a tampa livremente.
 */
function ApoioAGas({
  base,
  ponta,
  cortado,
}: {
  base: [number, number, number]
  ponta: [number, number, number]
  cortado: boolean
}) {
  if (!cortado) {
    return (
      <Barra
        id="tampaTraseira.apoio"
        de={vetor(...base)}
        para={vetor(...ponta)}
        largura={0.022}
        espessura={0.022}
        cilindrica
      />
    )
  }

  const emFracao = (t: number): [number, number, number] => [
    base[0] + (ponta[0] - base[0]) * t,
    base[1] + (ponta[1] - base[1]) * t,
    base[2] + (ponta[2] - base[2]) * t,
  ]
  const antesDoCorte = emFracao(0.44)
  const depoisDoCorte = emFracao(0.58)

  return (
    <group>
      <Barra
        id="tampaTraseira.apoio"
        de={vetor(...base)}
        para={vetor(...antesDoCorte)}
        largura={0.022}
        espessura={0.022}
        cilindrica
      />
      <Barra
        id="tampaTraseira.apoio"
        de={vetor(...depoisDoCorte)}
        para={vetor(...ponta)}
        largura={0.022}
        espessura={0.022}
        cilindrica
      />
      <mesh position={antesDoCorte}>
        <sphereGeometry args={[0.013, 10, 8]} />
        <MaterialDeCorte />
      </mesh>
      <mesh position={depoisDoCorte}>
        <sphereGeometry args={[0.013, 10, 8]} />
        <MaterialDeCorte />
      </mesh>
    </group>
  )
}

function Fascia({
  id,
  perfil,
  z,
  yTopo,
  afastamento,
  corAlvo,
}: {
  id?: string
  perfil: PerfilVeiculo
  z: number
  yTopo: number
  afastamento?: number
  corAlvo?: string
}) {
  const altura = yTopo - Y_BASE_FASCIA
  const posicao: [number, number, number] = [0, Y_BASE_FASCIA + altura / 2, z]
  // o mesmo campo recua o dianteiro (frente) e o traseiro (traseira): cada
  // um só sente o impacto da própria extremidade
  const geometria = useGeometriaDeformada(baseParaChoque(perfil, yTopo), 'paraChoque', { posicao })

  const corpo = (
    <mesh geometry={geometria} position={posicao}>
      {corAlvo ? (
        <PinturaAlvo cor={corAlvo} />
      ) : (
        <MaterialPeca id={id!} corBase={TONS_CARROCERIA.paraChoque} acabamento="pintura" />
      )}
    </mesh>
  )

  if (corAlvo) return corpo
  return (
    <Peca id={id!} deslocamentoAfastado={[0, 0, afastamento!]}>
      {corpo}
    </Peca>
  )
}

/**
 * Assoalho estrutural e fechamento inferior da carroceria (underbody floor pan).
 *
 * Fecha o vão inferior entre as soleiras e caixas de roda, desde o subquadro
 * dianteiro / cofre do motor até o para-choque traseiro, impedindo que o carro
 * fique oco por baixo e com o chão visível através da cabine.
 */
function AssoalhoCarroceria({
  perfil,
  cor,
  modoAlvo = false,
}: {
  perfil: PerfilVeiculo
  cor?: string
  modoAlvo?: boolean
}) {
  const zDianteiro = perfil.colunaA.zBase + 0.16
  const zTraseiro = perfil.traseiraZ + 0.14
  const comprimento = zDianteiro - zTraseiro
  const zCentro = (zDianteiro + zTraseiro) / 2
  const largura = perfil.largura - 0.14
  const yAssoalho = perfil.pisoY - 0.025
  const corBase = modoAlvo ? '#1e293b' : '#1e293b'

  return (
    <group data-testid="assoalho-carroceria">
      {/* Chapa principal contínua do assoalho entre as soleiras */}
      <mesh position={[0, yAssoalho, zCentro]}>
        <boxGeometry args={[largura, 0.03, comprimento]} />
        <meshStandardMaterial color={corBase} roughness={0.85} metalness={0.3} />
      </mesh>

      {/* Protetor inferior do cofre do motor / cárter */}
      <mesh
        position={[
          0,
          yAssoalho + 0.035,
          (zDianteiro + (perfil.frenteZ - 0.26)) / 2,
        ]}
      >
        <boxGeometry
          args={[
            perfil.largura - 0.32,
            0.025,
            (perfil.frenteZ - 0.26) - zDianteiro,
          ]}
        />
        <meshStandardMaterial color="#0f172a" roughness={0.9} metalness={0.4} />
      </mesh>

      {/* Nervuras longitudinais estampadas de reforço estrutural */}
      {[-0.32, 0.32].map((x) => (
        <mesh key={x} position={[x, yAssoalho - 0.015, zCentro]}>
          <boxGeometry args={[0.07, 0.018, comprimento - 0.3]} />
          <meshStandardMaterial color="#0f172a" roughness={0.9} metalness={0.35} />
        </mesh>
      ))}

      {/* Rebaixo central do escapamento / túnel estrutural sob o assoalho */}
      <mesh position={[0, yAssoalho + 0.035, zCentro]}>
        <boxGeometry args={[0.22, 0.05, comprimento - 0.15]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} metalness={0.4} />
      </mesh>
    </group>
  )
}


function DetalheExterno({ id, ativo, children }: { id: string; ativo: boolean; children: ReactNode }) {
  return ativo ? <Peca id={id}>{children}</Peca> : <>{children}</>
}

